import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectBillingSummary,
  selectBillingSummaryStatus,
} from "../../features/billing/billing-slice";
import { fetchBillingSummary } from "../../features/billing/slice-extensions/billing-slice-extension";
import { fetchContracts } from "../../features/contracts/slice-extensions/contracts-slice-extension";
import { selectIsOrgOwnerOrAdmin } from "../../features/authentication/authenticationSlice";
import { LoadingStatus } from "../../features/sessions/models/LoadingStatus";
import { ROUTES } from "../../routes";
import AttentionChip from "./AttentionChip";
import BillingFirstContractCard from "./BillingFirstContractCard";
import { CircleDollarSign, Sparkles } from "lucide-react";

/**
 * Dashboard widget: a compact "billing attention" card with the five count chips,
 * each click-through to the dedicated billing page filtered by status. Phase 6.
 */
const BillingAttentionCard: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Billing is an Owner/Admin concern — plain Instructors never see this card.
  const isOrgOwnerOrAdmin = useAppSelector(selectIsOrgOwnerOrAdmin);
  const summary = useAppSelector(selectBillingSummary);
  const status = useAppSelector(selectBillingSummaryStatus);
  // null = not probed yet; the first-contract card only shows once we know.
  const [contractsTotal, setContractsTotal] = useState<number | null>(null);

  useEffect(() => {
    if (isOrgOwnerOrAdmin && !summary && status === LoadingStatus.Idle) {
      dispatch(fetchBillingSummary());
    }
  }, [summary, status, dispatch, isOrgOwnerOrAdmin]);

  useEffect(() => {
    if (!isOrgOwnerOrAdmin) return;
    let active = true;
    dispatch(fetchContracts({ page: 1, pageSize: 1 }))
      .unwrap()
      .then((result) => {
        if (active) setContractsTotal(result.totalCount);
      })
      .catch(() => {
        // No probe, no card — the home page works without it.
      });
    return () => {
      active = false;
    };
  }, [dispatch, isOrgOwnerOrAdmin]);

  const goTo = (filter: string) => () => navigate(`${ROUTES.BILLING}?attention=${filter}`);
  const counts = summary?.counts;
  const missingPayments = summary?.contractsMissingPayment.length ?? 0;
  const total = counts
    ? counts.drafts + counts.overdue + counts.notYetRecorded + counts.lowBalance + counts.invoiced
      + missingPayments
    : 0;

  if (!isOrgOwnerOrAdmin) {
    return null;
  }

  // Nothing billed yet: the card's job is getting the first contract created.
  if (contractsTotal === 0 && total === 0) {
    return <BillingFirstContractCard onCreated={() => setContractsTotal(1)} />;
  }

  // Attention surface: only takes up dashboard space when something needs action.
  if (!counts || total === 0) {
    return null;
  }

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <h3 className="flex items-center gap-2">
        <CircleDollarSign size={20} className="text-grey-30 dark:text-grey-70" />
        {t("billing.attentionCard.title")}
      </h3>
      <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
        {t("billing.attentionCard.itemsAcross", { count: total })}
      </p>
      {counts && counts.drafts > 0 && (
        <button
          type="button"
          onClick={() => navigate(`${ROUTES.BILLING}?tab=month-end`)}
          className="mt-3 flex w-full items-center gap-2 rounded-xl bg-primary-95 px-3 py-2 text-left text-sm font-medium text-primary-30 hover:bg-primary-90 dark:bg-primary-20 dark:text-primary-95 dark:hover:bg-[#3E334D]"
        >
          <Sparkles size={16} className="shrink-0" />
          <span>
            {t("billing.attentionCard.invoicesReady", { count: counts.drafts })}
          </span>
        </button>
      )}
      {counts && (
        <div className="mt-4 flex flex-wrap gap-2">
          <AttentionChip label={t("billing.attention.drafts")} count={counts.drafts} variant="info" onClick={goTo("drafts")} />
          <AttentionChip label={t("billing.attention.overdue")} count={counts.overdue} variant="alert" onClick={goTo("overdue")} />
          <AttentionChip label={t("billing.attention.notYetRecorded")} count={counts.notYetRecorded} variant="warning" onClick={goTo("notYetRecorded")} />
          <AttentionChip label={t("billing.attention.lowBalance")} count={counts.lowBalance} variant="warning" onClick={goTo("lowBalance")} />
          <AttentionChip label={t("billing.attention.invoiced")} count={counts.invoiced} variant="neutral" onClick={goTo("invoiced")} />
          <AttentionChip label={t("billing.attention.missingPayment")} count={missingPayments} variant="alert" onClick={() => navigate(ROUTES.BILLING)} />
        </div>
      )}
    </div>
  );
};

export default BillingAttentionCard;
